<template>
  <div
    class="store border-b border-gray-400 p-5"
    :class="{
      'md:border-l-8 md:border-gray-400': isCurrent(store.id),
      'hover:cursor-pointer': !isCurrent(store.id),
    }"
    @click="openStorePopup(store)"
  >
    <div class="flex flex-col gap-3 bg-[#F1F1F0] p-6">
      <div class="flex w-full justify-between">
        <Headline tag="h5" look="h4">
          {{ headline }}
        </Headline>
      </div>
      <div>
        <p class="whitespace-pre-line">{{ text }}</p>
      </div>
      <div class="mt-2">
        <div class="flex flex-col gap-2">
          <FimButton
            :id="`store-${store.id}`"
            key="link-button"
            rel="nofollow"
            :to="link"
            @mousedown="onCTAClick"
          >
            <template #icon>
              <LazyIconFielmannCalendar class="size-6" />
            </template>
            {{ buttonLabel }}
          </FimButton>
          <FimButton
            :id="`store-${store.id}`"
            key="link-button"
            rel="nofollow"
            type="link-alternative"
            :to="detailsUrl(store)"
          >
            {{ $t('store_finder.new_store_opening.button_label') }}
          </FimButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IStore, IStoreActiveTeaser } from '~/rpcMethods/stores'
import { useMapInfoWindow } from '~/composables/useMapInfoWindow'

const props = defineProps({
  store: {
    type: Object as PropType<IStoreActiveTeaser>,
    required: true,
  },
})

const { currentStore, setCurrentStore } = useMapInfoWindow()
const $currentShop = useCurrentShop()
const $fimLocalePath = useFimLocalePath()
const $i18n = useI18n()
const { trackCTAClick } = await useTrackingEvents()

const underscoredLocale = getNormalizedLocale(
  $currentShop.value.locale,
  '_',
) as UnderscoredLocale

const isCurrent = (storeId: number) => currentStore.value?.id === storeId

const openStorePopup = (store: IStore) => {
  if (window && window.innerWidth > 768) {
    setCurrentStore(store)
  }
}

const detailsUrl = (store: IStore) => {
  return getStoreUrl(store, $fimLocalePath)
}

const headline = computed(() =>
  props.store.activeTeaser.headline[underscoredLocale]?.length
    ? props.store.activeTeaser.headline[underscoredLocale]
    : $i18n.t('store_finder.new_store_opening.headline'),
)

const text = computed(
  () => props.store.activeTeaser.text[underscoredLocale] ?? '',
)

const buttonLabel = computed(() =>
  props.store.activeTeaser.link.text[underscoredLocale]?.length
    ? props.store.activeTeaser.link.text[underscoredLocale]
    : $i18n.t('store_finder.new_store_opening.button_label'),
)

const link = computed(
  () => props.store.activeTeaser.link.url[underscoredLocale],
)

const onCTAClick = (event: any) => {
  trackCTAClick({
    element: event.target,
  })
}
</script>
